import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  CurrencyCircleDollar, Plus, Spinner, Trash, PencilSimple, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTariffsPage = () => {
  const { t } = useTranslation();
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minPrice, setMinPrice] = useState(0);
  const [minPriceInput, setMinPriceInput] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formName, setFormName] = useState("");
  const [formTiers, setFormTiers] = useState([{ min_kwh: 0, max_kwh: 10, price_per_kwh: "" }]);
  const [saving, setSaving] = useState(false);
  const [atPage, setAtPage] = useState(1);
  const [atPageSize, setAtPageSize] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      const [tariffsRes, priceRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/system-tariffs`),
        axios.get(`${API_URL}/api/admin/min-kwh-price`),
      ]);
      setTariffs(tariffsRes.data);
      setMinPrice(priceRes.data.min_kwh_price);
      setMinPriceInput(priceRes.data.min_kwh_price.toString());
    } catch { toast.error(t("tariffs.data_error")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveMinPrice = async () => {
    const val = parseFloat(minPriceInput);
    if (isNaN(val) || val < 0) { toast.error(t("tariffs.enter_valid_price")); return; }
    try {
      await axios.put(`${API_URL}/api/admin/min-kwh-price?value=${val}`);
      setMinPrice(val);
      toast.success(t("tariffs.min_price_updated"));
    } catch { toast.error(t("common.error")); }
  };

  const addTier = () => {
    const last = formTiers[formTiers.length - 1];
    setFormTiers([...formTiers, { min_kwh: last.max_kwh || 0, max_kwh: (last.max_kwh || 0) + 10, price_per_kwh: "" }]);
  };

  const removeTier = (i) => {
    if (formTiers.length <= 1) return;
    setFormTiers(formTiers.filter((_, idx) => idx !== i));
  };

  const updateTier = (i, field, value) => {
    const updated = [...formTiers];
    updated[i] = { ...updated[i], [field]: value };
    // Auto-chain: next tier min = this tier max
    if (field === "max_kwh" && i < updated.length - 1) {
      updated[i + 1] = { ...updated[i + 1], min_kwh: parseFloat(value) || 0 };
    }
    setFormTiers(updated);
  };

  const openCreate = () => {
    setEditId(null);
    setFormName("");
    setFormTiers([{ min_kwh: 0, max_kwh: 10, price_per_kwh: "" }]);
    setShowForm(true);
  };

  const openEdit = (tariff) => {
    setEditId(tariff.id);
    setFormName(tariff.name);
    setFormTiers(tariff.tiers.map((tier) => ({ ...tier, price_per_kwh: tier.price_per_kwh.toString() })));
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error(t("tariffs.enter_name")); return; }
    const tiers = formTiers.map((tier, i) => ({
      min_kwh: parseFloat(tier.min_kwh) || 0,
      max_kwh: i === formTiers.length - 1 ? null : (parseFloat(tier.max_kwh) || 0),
      price_per_kwh: parseFloat(tier.price_per_kwh) || 0,
    }));
    if (tiers.some((tier) => tier.price_per_kwh <= 0)) { toast.error(t("common.error")); return; }
    setSaving(true);
    try {
      const payload = { name: formName, tiers };
      if (editId) {
        await axios.put(`${API_URL}/api/admin/system-tariffs/${editId}`, payload);
        toast.success(t("tariffs.updated"));
      } else {
        await axios.post(`${API_URL}/api/admin/system-tariffs`, payload);
        toast.success(t("tariffs.created"));
      }
      setShowForm(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t("common.error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("tariffs.confirm_delete"))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/system-tariffs/${id}`);
      toast.success(t("tariffs.deleted"));
      fetchData();
    } catch { toast.error(t("tariffs.delete_error")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-tariffs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("tariffs.title")}</h1>
          <p className="text-sm text-slate-500">{t("tariffs.subtitle")}</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-tariff-btn">
          <Plus size={16} className="mr-2" /> {t("tariffs.new")}
        </Button>
      </div>

      {/* Min Price Setting */}
      <Card className="border-amber-200 bg-amber-50/30 shadow-sm">
        <CardContent className="p-4 flex items-center gap-4">
          <CurrencyCircleDollar size={24} className="text-amber-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">{t("tariffs.min_price")}</p>
            <p className="text-xs text-slate-500">{t("tariffs.min_price_desc")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="number" step="0.01" value={minPriceInput}
              onChange={(e) => setMinPriceInput(e.target.value)}
              className="w-24 text-center" data-testid="min-price-input" />
            <span className="text-sm text-slate-500">TL/kWh</span>
            <Button size="sm" onClick={handleSaveMinPrice} data-testid="save-min-price-btn">{t("common.save")}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tariff Form */}
      {showForm && (
        <Card className="border-indigo-200 shadow-sm" data-testid="tariff-form">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{editId ? t("tariffs.edit") : t("tariffs.new_form")}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X size={16} /></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">{t("tariffs.name")}</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)}
                placeholder="Orn: Standart Konut" data-testid="tariff-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t("tariffs.tiers_label")}</Label>
              {formTiers.map((tier, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                  <span className="text-xs text-slate-500 w-6">{i + 1}.</span>
                  <Input type="number" value={tier.min_kwh} onChange={(e) => updateTier(i, "min_kwh", e.target.value)}
                    className="w-20 text-sm" placeholder="Min" disabled={i > 0} />
                  <span className="text-xs text-slate-400">-</span>
                  {i === formTiers.length - 1 ? (
                    <span className="w-20 text-center text-xs text-slate-400">{t("tariffs.unlimited")}</span>
                  ) : (
                    <Input type="number" value={tier.max_kwh} onChange={(e) => updateTier(i, "max_kwh", e.target.value)}
                      className="w-20 text-sm" placeholder="Max" />
                  )}
                  <span className="text-xs text-slate-400">kWh</span>
                  <Input type="number" step="0.01" value={tier.price_per_kwh}
                    onChange={(e) => updateTier(i, "price_per_kwh", e.target.value)}
                    className="w-24 text-sm" placeholder="Fiyat" data-testid={`tier-price-${i}`} />
                  <span className="text-xs text-slate-400">TL/kWh</span>
                  {formTiers.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeTier(i)} className="text-red-500 h-8 w-8 p-0">
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addTier} className="w-full" data-testid="add-tier-btn">
                <Plus size={14} className="mr-1" /> {t("tariffs.add_tier")}
              </Button>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-tariff-btn">
                {saving ? <Spinner size={14} className="animate-spin mr-2" /> : null}
                {editId ? t("common.update") : t("common.create")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tariffs List */}
      {tariffs.length === 0 ? (
        <Card className="border-dashed border-slate-300"><CardContent className="p-8 text-center text-slate-500">{t("tariffs.no_tariffs")}</CardContent></Card>
      ) : (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {tariffs.slice((atPage - 1) * atPageSize, atPage * atPageSize).map((tariff) => (
            <Card key={tariff.id} className="border-slate-200 shadow-sm" data-testid={`tariff-card-${tariff.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{tariff.name}</h3>
                    <Badge className="bg-indigo-100 text-indigo-700 text-xs">{t("staff.system")}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(tariff)} data-testid={`edit-tariff-${tariff.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(tariff.id)} className="text-red-500" data-testid={`delete-tariff-${tariff.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-1.5 text-slate-500">{t("tariffs.tier")}</th>
                      <th className="text-left py-1.5 text-slate-500">{t("tariffs.range")}</th>
                      <th className="text-right py-1.5 text-slate-500">{t("tariffs.price")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tariff.tiers.map((tier, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1.5 text-slate-700">{i + 1}. {t("tariffs.tier")}</td>
                        <td className="py-1.5 text-slate-600">{tier.min_kwh} - {tier.max_kwh ?? "+"} kWh</td>
                        <td className="py-1.5 text-right font-medium text-slate-900">{"\u20ba"}{tier.price_per_kwh}/kWh</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
        </div>
        <Pagination total={tariffs.length} page={atPage} pageSize={atPageSize} onPageChange={setAtPage} onPageSizeChange={setAtPageSize} />
        </>
      )}
    </div>
  );
};

export default AdminTariffsPage;
